// src/components/crm/RecurringInvoiceFormModal.js  recinvform-9f3c2a71
// CREATE / EDIT form for recurring-invoice schedules (Seam 2b-1). Mirrors
// InvoiceFormModal.js for everything it already solves (customer picker,
// service-location picker + forced choice, line-item editor, save-time
// taxSnapshot, doc()+setDoc write, hand-rolled chrome) and JobFormModal.js's
// recurrence controls — but NEVER reuses buildCleanRecurrence (see the big
// comment on buildScheduleRecurrence below). No pause/skip/end here (2b-2).
import React, { useState, useEffect, useMemo } from 'react';
import { db, functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { addDays } from 'date-fns'; // startOfDay dropped: orgtime-7c2e4b19
import { X, Trash2, Plus, Loader2, Repeat, Search, Wrench, Package, AlertTriangle } from 'lucide-react';
import { canSeeAllCustomers } from '../../utils/permissions'; // officecust-9a4d2c61
import { buildAddressString } from '../../utils/address';
import { formatCurrency, formatLocalDate } from '../../utils/formatters'; // localstamp-6a1f7e42; parseLocalDate dropped: orgtime-7c2e4b19
import { pickerItems } from '../../utils/catalogUi'; // catscreen-7a2c4e91 — pickers hide archived items
import { advanceRecurrence } from '../../utils/recurrence';
import * as lineIds from '../../utils/lineIds'; // lineidweb-9e4a2d17 — permanent per-line IDs
import { resolveOrgTimeZone, orgDayStart, orgDayEnd, orgEndOfDay, orgDateYmd } from '../../utils/orgTime'; // orgtime-7c2e4b19
import LineDescriptionInput from '../documents/LineDescriptionInput'; // linetext-4d8b2e61 — Enter, indents and "- " bullets in line descriptions

// "Never ends" storage — the year-2999 sentinel, byte-identical to the seed
// script (seedRecurringInvoice.js SENTINEL) and the generator (index.js
// NEVER_SENTINEL). Stored ALONGSIDE neverEnds:true; the flag hides it in the UI.
const RECURRENCE_END_SENTINEL = new Date('2999-12-31T00:00:00');
const UNITS = ['days', 'weeks', 'months', 'years'];

// yyyy-MM-dd for a <input type="date"> from a Date | Firestore Timestamp | string.
// orgtime-7c2e4b19 — the COMPANY day the stored date means (orgDateYmd: an end-of-day value is its
// day, a midnight — the company's or a browser's in another zone — is the nearest one). Was the UTC
// day of the stored instant.
const toDateInputValue = (field, tz) => orgDateYmd(field, tz) || '';

// "Nov 4, 2026" for a 'YYYY-MM-DD' company day, whatever zone the browser is in.
const fmtYmd = (ymd) => (ymd ? new Date(`${ymd}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '');

// A Custom-only recurrence, using the SAME date-step engine the generator uses.
const stepRec = (interval, unit) => ({ frequency: 'Custom', interval, unit });

// Snap a picked end date DOWN to the last real occurrence <= it (copied from
// JobFormModal's snapEndToCycle so the stored end never lies about the last visit).
// orgtime-7c2e4b19 — run dates are compared as COMPANY days (orgDateYmd, the same reading the
// server generator uses) and the result is the END of the last run's company day.
// `start` is the company's midnight on the start day (orgDayStart).
const snapEndToCycle = (start, interval, unit, pickedEnd, tz) => {
    if (!start || !pickedEnd) return pickedEnd;
    const pickYmd = orgDateYmd(pickedEnd, tz);
    let cur = new Date(start);
    let last = new Date(cur);
    let guard = 0;
    while (guard++ < 2000) {
        const nx = advanceRecurrence(cur, stepRec(interval, unit), start);
        if (!nx || orgDateYmd(nx, tz) > pickYmd) break;
        cur = nx; last = new Date(cur);
    }
    return orgDayEnd(orgDateYmd(last, tz), tz);
};

// Enumerate the first few occurrences for the live preview. → company days ('YYYY-MM-DD').
const enumerateOccurrences = (start, rec, max, tz) => {
    if (!start) return [];
    const out = [];
    let cur = new Date(start);
    const endYmd = rec.neverEnds || !rec.endDate ? null : orgDateYmd(rec.endDate, tz); // orgtime-7c2e4b19
    let guard = 0;
    while (guard++ < 2000 && out.length < max) {
        if (endYmd && orgDateYmd(cur, tz) > endYmd) break;
        out.push(orgDateYmd(cur, tz));
        const nx = advanceRecurrence(cur, stepRec(rec.interval, rec.unit), start);
        if (!nx) break;
        cur = nx;
    }
    return out;
};

// CANONICAL SHAPE FOR SCHEDULES — ALWAYS Custom. Do NOT replace this with
// JobFormModal's buildCleanRecurrence: that collapses a bounded every-1-month
// rule to frequency:'Monthly' and DROPS interval/unit. The invoice generator
// reads interval/unit, and the Monthly PRESET path in advanceRecurrence carries
// a month-end degradation the Custom path does not — a schedule built the preset
// way would bill on the wrong day. One encoding only: Custom. recinvform-9f3c2a71
const buildScheduleRecurrence = (rec, start, tz) => {
    const interval = Math.max(1, parseInt(rec.interval, 10) || 1);
    const unit = UNITS.includes(rec.unit) ? rec.unit : 'months';
    const neverEnds = !!rec.neverEnds;
    const endDate = neverEnds
        ? RECURRENCE_END_SENTINEL
        : snapEndToCycle(start, interval, unit, rec.endDate, tz);
    // endDate is stored as an EXPLICIT Firestore Timestamp to match the seed
    // script (seedRecurringInvoice.js:149 Timestamp.fromDate(endDate)) exactly;
    // the generator calls .endDate.toDate(). This field decides when billing
    // stops, so it does not ride on SDK date coercion. recinvform-9f3c2a71
    return { frequency: 'Custom', interval, unit, neverEnds, endDate: Timestamp.fromDate(endDate) };
};

const RecurringInvoiceFormModal = ({ closeModal, editingSchedule, currentUserProfile, companySettings, customers, user, services }) => {
    // localstamp-6a1f7e42 — `today` (the default startDate seed) is derived INSIDE the
    // component with formatLocalDate: fresh on every open, not frozen at tab load, and the
    // LOCAL calendar day, not UTC. The seed is stored as a real Timestamp and read by the
    // backend generator, so a wrong seed would anchor the whole schedule on the wrong day.
    // orgtime-7c2e4b19 — every DAY on this form (start, end, preview) is a day on the COMPANY's
    // calendar; the start is stored as the company's midnight, the end as the end of the company day.
    const orgTz = resolveOrgTimeZone(companySettings?.timezone);
    const today = formatLocalDate();
    const defaultLineItem = { serviceId: '', name: '', description: '', quantity: 1, price: 0, taxExempt: false, type: 'Service', taxCodeId: '' };

    const [taxOptions, setTaxOptions] = useState([]);
    const [isLoadingTax, setIsLoadingTax] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [serviceLocation, setServiceLocation] = useState('');
    const [nameTouched, setNameTouched] = useState(() => !!editingSchedule?.name);

    const [scheduleData, setScheduleData] = useState({
        name: '',
        title: 'Invoice',
        customerId: '', customerName: '', customerDetails: {},
        propertyId: null, propertyName: null, serviceAddress: null,
        billTo: null,
        lineItems: [defaultLineItem],
        notes: '', workPerformed: '',
        recurrence: { interval: 1, unit: 'months', neverEnds: true, endDate: RECURRENCE_END_SENTINEL },
        startDate: today,
        runType: 'auto_send',
        billingMode: 'advance',
    });

    const isEdit = !!editingSchedule?.id;
    // Start date is the billing anchor. It stays editable until the schedule has
    // generated its first invoice (lastRunDate null/absent); after that, invoices
    // exist against this anchor and moving it is unsafe, so it locks. recinvform-9f3c2a71
    const startEditable = !isEdit || !editingSchedule?.lastRunDate;

    // officecust-9a4d2c61 — WHO SEES WHICH CUSTOMERS in this picker (founder decision 2026-10-04):
    // Office Staff + every high-level role see all; Sales Consultants see their own. AND the
    // customer already on the form (a job's / estimate's customer, or the one being edited) is
    // ALWAYS in the list, so a seeded invoice can never open with "Select Customer…" — the bug
    // Office Staff hit creating an invoice from a job (same idea as JobFormModal's
    // ensureCurrentCustomer). If that customer is somehow not in the loaded list, it is read by id
    // (firestore.rules lets every staff member read any customer of their company).
    const currentCustomerId = scheduleData.customerId;
    const [fetchedCustomer, setFetchedCustomer] = useState(null);
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId || !currentCustomerId || (customers || []).some(c => c.id === currentCustomerId)) return undefined;
        let cancelled = false;
        getDoc(doc(db, `organizations/${orgId}/customers`, currentCustomerId))
            .then(snap => { if (!cancelled && snap.exists()) setFetchedCustomer({ ...snap.data(), id: snap.id }); })
            .catch(err => console.warn('Could not load the form\'s customer by id', err));
        return () => { cancelled = true; };
    }, [currentCustomerId, customers, currentUserProfile?.organizationId]);
    const availableCustomers = useMemo(() => {
        if (!currentUserProfile) return [];
        const all = customers || [];
        const base = canSeeAllCustomers(currentUserProfile)
            ? all
            : all.filter(c => c.sales_person_id === currentUserProfile.id);
        if (!currentCustomerId || base.some(c => c.id === currentCustomerId)) return base;
        const current = all.find(c => c.id === currentCustomerId)
            || (fetchedCustomer && fetchedCustomer.id === currentCustomerId ? fetchedCustomer : null);
        return current ? [current, ...base] : base;
    }, [customers, currentUserProfile, currentCustomerId, fetchedCustomer]);

    useEffect(() => {
        const fetchTaxCodes = async () => {
            if (!currentUserProfile?.organizationId) return;
            setIsLoadingTax(true);
            try {
                const getOptions = httpsCallable(functions, 'getQuickBooksMappingOptions');
                const result = await getOptions();
                if (result.data?.taxCodes) setTaxOptions(result.data.taxCodes);
            } catch (e) { console.error('Failed to load tax codes:', e); }
            finally { setIsLoadingTax(false); }
        };
        fetchTaxCodes();
    }, [currentUserProfile]);

    // Initialize — EDIT hydrates from the stored doc; NEW starts blank.
    useEffect(() => {
        if (isEdit) {
            const stored = editingSchedule.recurrence || {};
            const storedEnd = stored.endDate && typeof stored.endDate.toDate === 'function'
                ? stored.endDate.toDate()
                : (stored.endDate ? new Date(stored.endDate) : null);
            // Sentinel hydration (mirrors JobFormModal:339-354): treat neverEnds
            // === true OR a stored end year >= 2900 as ongoing, and show a sane
            // fallback date so the user never sees 2999 if they un-tick.
            const neverEnds = stored.neverEnds === true || (storedEnd instanceof Date && storedEnd.getFullYear() >= 2900);
            const fallbackEnd = orgEndOfDay(new Date(new Date().setMonth(new Date().getMonth() + 3)), orgTz); // orgtime-7c2e4b19
            setScheduleData({
                ...editingSchedule,
                lineItems: (editingSchedule.lineItems || []).map(i => ({ ...defaultLineItem, ...i })),
                recurrence: {
                    interval: stored.interval || 1,
                    unit: UNITS.includes(stored.unit) ? stored.unit : 'months',
                    neverEnds,
                    endDate: neverEnds ? fallbackEnd : (storedEnd || fallbackEnd),
                },
                startDate: toDateInputValue(editingSchedule.startDate, orgTz) || today,
                // Every schedule has a runType; the fallback matches CREATION
                // (auto_send) so it can never silently flip an auto-send schedule
                // to draft on save if the field were ever missing.
                runType: editingSchedule.runType || 'auto_send',
                billingMode: editingSchedule.billingMode || 'advance',
            });
            setServiceLocation(editingSchedule.propertyId ? editingSchedule.propertyId : 'main');
            setNameTouched(true);
        }
    }, [editingSchedule, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

    // Suggest a schedule name from customer + location until the user edits it.
    const suggestName = (customerName, propertyName) => {
        if (!customerName) return '';
        return propertyName ? `${customerName} — ${propertyName}` : `${customerName} — Recurring`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setScheduleData(prev => ({ ...prev, [name]: value }));
    };

    const handleCustomerChange = (e) => {
        const customerId = e.target.value;
        const customer = customers.find(c => c.id === customerId);
        const hasProperties = (customer?.properties || []).length > 0;
        setScheduleData(prev => ({
            ...prev,
            customerId,
            customerName: customer ? customer.name : '',
            customerDetails: customer ? {
                name: customer.name, email: customer.email || '', phone: customer.phone || '',
                address: [customer.address1, customer.address2, customer.city].filter(Boolean).join(', '),
            } : {},
            propertyId: null, propertyName: null, serviceAddress: null,
            name: nameTouched ? prev.name : suggestName(customer?.name, null),
        }));
        setServiceLocation(hasProperties ? '' : 'main');
    };

    const handleServiceLocationChange = (e) => {
        const value = e.target.value;
        setServiceLocation(value);
        const customer = customers.find(c => c.id === scheduleData.customerId);
        const prop = (value && value !== 'main') ? (customer?.properties || []).find(p => p.id === value) : null;
        setScheduleData(prev => ({
            ...prev,
            propertyId: prop ? prop.id : null,
            propertyName: prop ? (prop.name || '') : null,
            serviceAddress: prop ? (prop.addressCanonical || buildAddressString(prop)) : null,
            name: nameTouched ? prev.name : suggestName(prev.customerName, prop ? (prop.name || '') : null),
        }));
    };

    const handleNameChange = (e) => { setNameTouched(true); setScheduleData(prev => ({ ...prev, name: e.target.value })); };

    // ── Recurrence controls ──────────────────────────────────────────────────
    const setRec = (patch) => setScheduleData(prev => ({ ...prev, recurrence: { ...prev.recurrence, ...patch } }));
    const handleIntervalChange = (e) => setRec({ interval: e.target.value });
    const handleUnitChange = (e) => setRec({ unit: e.target.value });
    const handleNeverEndsChange = (e) => setRec({ neverEnds: e.target.checked });
    const handleEndDateChange = (e) => {
        // orgtime-7c2e4b19 — the END of the picked company day (was the UTC parse: the day before in Canada).
        const picked = e.target.value ? orgDayEnd(e.target.value, orgTz) : null;
        const start = orgDayStart(scheduleData.startDate, orgTz);
        setRec({ endDate: picked ? snapEndToCycle(start, scheduleData.recurrence.interval, scheduleData.recurrence.unit, picked, orgTz) : picked });
    };

    // ── Line items (mirrors InvoiceFormModal) ────────────────────────────────
    const handleLineItemChange = (index, e) => {
        const { name, value, type, checked } = e.target;
        const items = [...scheduleData.lineItems];
        const item = { ...items[index] };
        if (type === 'checkbox') item[name] = checked;
        else item[name] = (name === 'quantity' || name === 'price') ? (value === '' ? '' : parseFloat(value)) : value;
        items[index] = item;
        setScheduleData(prev => ({ ...prev, lineItems: items }));
    };
    const handleServiceSelect = (index, serviceId) => {
        const service = services?.find(s => s.id === serviceId);
        const items = [...scheduleData.lineItems];
        items[index] = service
            ? { ...items[index], serviceId, name: service.name, description: service.description, price: service.price, type: service.type || 'Service', taxCodeId: service.taxCodeId || '', quantity: 1 }
            : { ...items[index], serviceId: '', taxCodeId: '' };
        setScheduleData(prev => ({ ...prev, lineItems: items }));
    };
    const addLineItem = () => setScheduleData(prev => ({ ...prev, lineItems: [...(prev.lineItems || []), lineIds.newLine(defaultLineItem, lineIds.newLineId)] })); // lineidweb-9e4a2d17 — a fresh object with its own lineId
    const removeLineItem = (index) => setScheduleData(prev => ({ ...prev, lineItems: prev.lineItems.filter((_, i) => i !== index) }));

    const calculations = useMemo(() => {
        let subtotal = 0, totalTaxAmount = 0;
        scheduleData.lineItems.forEach(item => {
            const lineTotal = (Number(item.quantity) || 0) * (Number(item.price) || 0);
            subtotal += lineTotal;
            if (!item.taxExempt) {
                let specificTax = taxOptions.find(t => t.Id === item.taxCodeId);
                if (!specificTax && !item.taxCodeId && companySettings?.quickbooksSettings?.defaultTaxCodeId) {
                    specificTax = taxOptions.find(t => t.Id === companySettings.quickbooksSettings.defaultTaxCodeId);
                }
                const rate = specificTax ? specificTax.Rate : (companySettings?.taxRate || 0);
                totalTaxAmount += lineTotal * (rate / 100);
            }
        });
        return { subtotal, taxAmount: totalTaxAmount, total: subtotal + totalTaxAmount };
    }, [scheduleData.lineItems, companySettings, taxOptions]);

    // ── Live recurrence + billing-period preview ─────────────────────────────
    const preview = useMemo(() => {
        const start = orgDayStart(scheduleData.startDate, orgTz); // orgtime-7c2e4b19
        if (!start || isNaN(start.getTime())) return null;
        const rec = { interval: Math.max(1, parseInt(scheduleData.recurrence.interval, 10) || 1), unit: scheduleData.recurrence.unit, neverEnds: scheduleData.recurrence.neverEnds, endDate: scheduleData.recurrence.endDate };
        const occ = enumerateOccurrences(start, rec, rec.neverEnds ? 4 : 500, orgTz);
        if (occ.length === 0) return null;
        const fmt = (d) => fmtYmd(orgDateYmd(d, orgTz)); // a run date → its company day
        const firstFew = occ.slice(0, 4).map(fmtYmd).join(', ');
        const summary = rec.neverEnds
            ? `Ongoing — ${firstFew}…`
            : `${occ.length} invoice${occ.length === 1 ? '' : 's'}: ${firstFew}${occ.length > 4 ? '…' : ''} — last ${fmtYmd(occ[occ.length - 1])}`;

        // First billing period — replicates the generator (index.js:1530-1547).
        const s0 = start;
        const runDate = scheduleData.billingMode === 'arrears' ? advanceRecurrence(s0, stepRec(rec.interval, rec.unit), start) : s0;
        const nextRun = advanceRecurrence(runDate, stepRec(rec.interval, rec.unit), start);
        let periodStart, periodEnd;
        if (scheduleData.billingMode === 'arrears') { periodStart = s0; periodEnd = addDays(runDate, -1); }
        else { periodStart = runDate; periodEnd = addDays(nextRun, -1); }
        const period = `First invoice generates ${fmt(runDate)} and bills for ${fmt(periodStart)} – ${fmt(periodEnd)} (${scheduleData.billingMode === 'arrears' ? 'in arrears' : 'in advance'}).`;
        return { summary, period };
    }, [scheduleData.startDate, scheduleData.recurrence, scheduleData.billingMode, orgTz]);

    // ── Save ─────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!scheduleData.name.trim()) { setError('Please give this schedule a name.'); return; }
        if (!scheduleData.customerId) { setError('Please select a customer.'); return; }
        const selCust = customers.find(c => c.id === scheduleData.customerId);
        if ((selCust?.properties || []).length > 0 && serviceLocation === '') {
            setError('This customer has multiple service locations. Please choose which one this schedule is for.'); return;
        }
        const hasRealLine = scheduleData.lineItems.some(i => (Number(i.quantity) || 0) * (Number(i.price) || 0) > 0);
        if (!hasRealLine) { setError('Add at least one line item with an amount.'); return; }
        if (!scheduleData.startDate) { setError('Please choose a start date.'); return; }

        const start = orgDayStart(scheduleData.startDate, orgTz); // orgtime-7c2e4b19 — the company's midnight
        if (!scheduleData.recurrence.neverEnds) {
            const endYmd = scheduleData.recurrence.endDate ? orgDateYmd(scheduleData.recurrence.endDate, orgTz) : null;
            if (!endYmd || endYmd <= scheduleData.startDate) { setError('The end date must be after the start date (or tick "Never ends").'); return; }
        }
        setError('');
        setIsSaving(true);

        const orgId = currentUserProfile.organizationId;
        const path = `organizations/${orgId}/recurringInvoices`;

        // taxSnapshot {rate, name} computed AT SAVE TIME — identical to
        // InvoiceFormModal, matching the schedule/generator schema.
        const processedLineItems = scheduleData.lineItems.map(item => {
            let rate = 0, name = 'Tax';
            if (!item.taxExempt) {
                let specificTax = taxOptions.find(t => t.Id === item.taxCodeId);
                if (!specificTax && !item.taxCodeId && companySettings?.quickbooksSettings?.defaultTaxCodeId) {
                    specificTax = taxOptions.find(t => t.Id === companySettings.quickbooksSettings.defaultTaxCodeId);
                }
                if (specificTax) { rate = specificTax.Rate; name = specificTax.Name; }
                else { rate = companySettings?.taxRate || 0; name = `Tax (${rate}%)`; }
            } else { name = 'Exempt'; }
            return { ...item, taxSnapshot: { rate, name } };
        });

        const recurrence = buildScheduleRecurrence(scheduleData.recurrence, start, orgTz);

        // Anchor + first run, EXPLICIT Timestamps (match seedRecurringInvoice.js).
        // Arrears runs one cycle after the start so the first bill covers a
        // COMPLETED period; the generator flags start==nextRunDate arrears as
        // broken (index.js:1550), so we offset here.
        const startTs = Timestamp.fromDate(start);
        const nextRunDate = scheduleData.billingMode === 'arrears'
            ? advanceRecurrence(start, stepRec(recurrence.interval, recurrence.unit), start)
            : start;
        const nextRunTs = Timestamp.fromDate(nextRunDate);

        // EDITABLE FIELDS ONLY — template + rule. On edit these merge over the
        // existing doc; operational fields are DELIBERATELY absent so setDoc(merge)
        // never rewrites them. The ONE exception is the narrow re-anchor case
        // handled below. recinvform-9f3c2a71
        const editable = {
            name: scheduleData.name.trim(),
            title: scheduleData.title || 'Invoice',
            customerId: scheduleData.customerId,
            customerName: scheduleData.customerName,
            customerDetails: scheduleData.customerDetails || {},
            propertyId: scheduleData.propertyId || null,
            propertyName: scheduleData.propertyName || null,
            serviceAddress: scheduleData.serviceAddress || null,
            billTo: null,
            lineItems: lineIds.ensureLineIds(processedLineItems, lineIds.newLineId), // lineidweb-9e4a2d17 — each invoice the schedule makes points back at these IDs
            notes: scheduleData.notes || '',
            workPerformed: scheduleData.workPerformed || '',
            recurrence,
            runType: scheduleData.runType,
            billingMode: scheduleData.billingMode,
            updatedAt: serverTimestamp(),
        };

        try {
            if (isEdit) {
                // RE-ANCHOR (the only edit that touches an operational field):
                // when the schedule has NEVER generated an invoice (lastRunDate
                // null → startEditable true), the start date is still editable, so
                // fix the mistype and recompute nextRunDate with the SAME
                // advance/arrears logic as creation. A schedule that has already
                // run has startEditable === false, so neither startDate nor
                // nextRunDate is written and both stay frozen. recinvform-9f3c2a71
                if (startEditable) {
                    editable.startDate = startTs;
                    editable.nextRunDate = nextRunTs;
                }
                await setDoc(doc(db, path, editingSchedule.id), editable, { merge: true });
            } else {
                const newRef = doc(collection(db, path));
                await setDoc(newRef, {
                    ...editable,
                    id: newRef.id,
                    startDate: startTs,
                    nextRunDate: nextRunTs,
                    lastRunDate: null,
                    skipDates: [],
                    status: 'active',
                    unpaidGeneratedCount: 0,
                    oldestUnpaidDate: null,
                    lastError: null,
                    lastErrorAt: null,
                    createdAt: serverTimestamp(),
                    createdBy: user.uid,
                    endedAt: null,
                });
            }
            setIsSaving(false);
            closeModal(true);
        } catch (err) {
            console.error('Error saving recurring schedule:', err);
            setError(err.message || 'An unknown error occurred.');
            setIsSaving(false);
        }
    };

    const propsForCust = (customers.find(c => c.id === scheduleData.customerId)?.properties) || [];

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-0 md:p-4 backdrop-blur-sm">
            <div className="bg-white w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl md:rounded-xl shadow-2xl flex flex-col overflow-hidden">
                {/* HEADER */}
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 flex-shrink-0">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <Repeat size={18} /> {isEdit ? 'Edit Recurring Invoice' : 'New Recurring Invoice'}
                    </h2>
                    <button onClick={() => closeModal()} className="p-2 hover:bg-gray-200 rounded-full"><X size={20} className="text-gray-600" /></button>
                </div>

                {/* BODY */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50/50 space-y-6">
                    {/* DETAILS */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Schedule Name <span className="text-red-500">*</span></label>
                            <input value={scheduleData.name} onChange={handleNameChange} placeholder="e.g. Smith Rentals — 12 Main St" className="w-full p-2.5 border rounded-lg text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Customer <span className="text-red-500">*</span></label>
                            {isEdit ? (
                                <div className="p-2 bg-gray-50 rounded border border-gray-200">
                                    <p className="font-bold text-gray-900">{scheduleData.customerDetails?.name || scheduleData.customerName}</p>
                                </div>
                            ) : (
                                <select name="customerId" value={scheduleData.customerId} onChange={handleCustomerChange} className="w-full p-2.5 border rounded-lg bg-white text-sm">
                                    <option value="">Select Customer...</option>
                                    {availableCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Invoice Title</label>
                            <input name="title" value={scheduleData.title} onChange={handleChange} className="w-full p-2.5 border rounded-lg text-sm" placeholder="Shown on each generated invoice" />
                        </div>
                        {propsForCust.length > 0 && (
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Service Location <span className="text-red-500">*</span></label>
                                <select value={serviceLocation} onChange={handleServiceLocationChange}
                                    className={`w-full p-2.5 border rounded-lg bg-white text-sm ${serviceLocation === '' ? 'border-red-400' : ''}`}>
                                    <option value="">-- Choose a service location --</option>
                                    <option value="main">Main address</option>
                                    {propsForCust.map(p => <option key={p.id} value={p.id}>{(p.name || p.address1 || 'Unnamed property')} — {p.addressCanonical || buildAddressString(p)}</option>)}
                                </select>
                                {serviceLocation === '' && <p className="mt-1 text-xs text-red-500">This customer has multiple locations — choose which one.</p>}
                            </div>
                        )}
                    </div>

                    {/* LINE ITEMS */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Line Items</h3>
                            <button onClick={addLineItem} className="text-blue-600 text-sm font-medium hover:underline flex items-center gap-1"><Plus size={16} /> Add Item</button>
                        </div>
                        <div className="space-y-3">
                            {scheduleData.lineItems.map((item, index) => (
                                <div key={index} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 relative">
                                    <button onClick={() => removeLineItem(index)} className="absolute top-2 right-2 p-1.5 text-gray-300 hover:text-red-500 rounded-full"><Trash2 size={16} /></button>
                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                        <div className="col-span-12 md:col-span-3">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Catalog Item</label>
                                            <div className="relative">
                                                <Search size={14} className="absolute left-2.5 top-3 text-gray-400" />
                                                <select value={item.serviceId} onChange={(e) => handleServiceSelect(index, e.target.value)} className="w-full pl-8 p-2 border rounded-lg text-sm bg-gray-50">
                                                    <option value="">Custom Entry</option>
                                                    {pickerItems(services, item.serviceId).map(s => <option key={s.id} value={s.id}>{s.pickerLabel}</option>)/* catscreen-7a2c4e91 */}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-span-12 md:col-span-5">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Item</label>
                                            <input name="name" value={item.name} onChange={(e) => handleLineItemChange(index, e)} placeholder="Item Name" disabled={!!item.serviceId}
                                                className={`w-full p-2 border rounded-lg text-sm font-medium ${item.serviceId ? 'bg-gray-100 text-gray-600' : ''}`} />
                                            <LineDescriptionInput name="description" value={item.description} onChange={(e) => handleLineItemChange(index, e)}
                                                labelClassName="text-[10px] font-bold text-gray-400 uppercase mb-1 block" />
                                        </div>
                                        <div className="col-span-6 md:col-span-2">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Qty</label>
                                            <input type="number" name="quantity" value={item.quantity} onChange={(e) => handleLineItemChange(index, e)} className="w-full p-2 border rounded-lg text-center font-semibold" />
                                        </div>
                                        <div className="col-span-6 md:col-span-2">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Price</label>
                                            <div className="relative">
                                                <span className="absolute left-2 top-2 text-gray-400 text-xs">$</span>
                                                <input type="number" name="price" value={item.price} onChange={(e) => handleLineItemChange(index, e)} className="w-full p-2 pl-5 border rounded-lg text-right font-semibold" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-2 border-t border-gray-100 flex flex-wrap gap-4 items-center">
                                        <div className="flex items-center gap-2">
                                            <div className={`p-1 rounded ${item.type === 'Material' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                                                {item.type === 'Material' ? <Package size={14} /> : <Wrench size={14} />}
                                            </div>
                                            <select name="type" value={item.type || 'Service'} onChange={(e) => handleLineItemChange(index, e)} className="text-xs border-0 bg-transparent font-medium text-gray-700">
                                                <option value="Service">Service</option>
                                                <option value="Material">Material</option>
                                            </select>
                                        </div>
                                        {!item.taxExempt && (
                                            <select name="taxCodeId" value={item.taxCodeId || ''} onChange={(e) => handleLineItemChange(index, e)} className="p-1 border rounded-md text-xs w-24 bg-white ml-auto" disabled={isLoadingTax}>
                                                <option value="">Default Tax</option>
                                                {taxOptions.map(code => <option key={code.Id} value={code.Id}>{code.Name}</option>)}
                                            </select>
                                        )}
                                        <div className="flex items-center ml-auto md:ml-2">
                                            <input type="checkbox" name="taxExempt" id={`taxExempt-${index}`} checked={item.taxExempt} onChange={(e) => handleLineItemChange(index, e)} className="h-3 w-3 border-gray-300 rounded" />
                                            <label htmlFor={`taxExempt-${index}`} className="ml-1 text-xs text-gray-500 select-none">Tax Exempt</label>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RECURRENCE */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 space-y-3">
                        <div className="flex items-center gap-2"><Repeat size={16} className="text-gray-500" /><span className="text-sm font-semibold text-gray-700">Billing Schedule</span></div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Start Date <span className="text-red-500">*</span></label>
                            <input type="date" name="startDate" value={scheduleData.startDate} onChange={handleChange} disabled={!startEditable}
                                className="w-full md:max-w-xs p-2.5 border rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500" />
                            {isEdit && !startEditable && (
                                <p className="mt-1 text-xs text-gray-400">This schedule has already generated invoices, so the start date (its billing anchor) is locked.</p>
                            )}
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-700">Every</span>
                            <input type="number" min="1" max="999" value={scheduleData.recurrence.interval} onChange={handleIntervalChange} className="w-20 p-2.5 border rounded-lg text-sm" />
                            <select value={scheduleData.recurrence.unit} onChange={handleUnitChange} className="p-2.5 border rounded-lg bg-white text-sm">
                                <option value="days">{parseInt(scheduleData.recurrence.interval, 10) === 1 ? 'day' : 'days'}</option>
                                <option value="weeks">{parseInt(scheduleData.recurrence.interval, 10) === 1 ? 'week' : 'weeks'}</option>
                                <option value="months">{parseInt(scheduleData.recurrence.interval, 10) === 1 ? 'month' : 'months'}</option>
                                <option value="years">{parseInt(scheduleData.recurrence.interval, 10) === 1 ? 'year' : 'years'}</option>
                            </select>
                        </div>

                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                            <input type="checkbox" checked={!!scheduleData.recurrence.neverEnds} onChange={handleNeverEndsChange} className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                            Never ends
                        </label>

                        {!scheduleData.recurrence.neverEnds && (
                            <div className="md:max-w-xs">
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Ends on</label>
                                <input type="date" value={toDateInputValue(scheduleData.recurrence.endDate, orgTz)} onChange={handleEndDateChange} className="w-full p-2.5 border rounded-lg text-sm" />
                            </div>
                        )}

                        {preview && (
                            <div className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 space-y-1">
                                <p>{preview.summary}</p>
                                <p className="text-gray-500">{preview.period}</p>
                            </div>
                        )}
                    </div>

                    {/* DELIVERY + BILLING MODE */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">When each invoice is created</label>
                            <div className="space-y-2">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="radio" name="runType" value="auto_send" checked={scheduleData.runType === 'auto_send'} onChange={handleChange} className="mt-1 text-blue-600" />
                                    <span className="text-sm text-gray-700"><span className="font-medium">Send it automatically</span><br /><span className="text-xs text-gray-500">The invoice is created and emailed to the customer on each billing date.</span></span>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="radio" name="runType" value="draft" checked={scheduleData.runType === 'draft'} onChange={handleChange} className="mt-1 text-blue-600" />
                                    <span className="text-sm text-gray-700"><span className="font-medium">Create a draft for me to review</span><br /><span className="text-xs text-gray-500">The invoice is created but not sent — you send it yourself.</span></span>
                                </label>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Billing period</label>
                            <div className="space-y-2">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="radio" name="billingMode" value="advance" checked={scheduleData.billingMode === 'advance'} onChange={handleChange} className="mt-1 text-blue-600" />
                                    <span className="text-sm text-gray-700"><span className="font-medium">Bill in advance</span><br /><span className="text-xs text-gray-500">Each invoice covers the period about to start (a Feb 1 invoice covers Feb).</span></span>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="radio" name="billingMode" value="arrears" checked={scheduleData.billingMode === 'arrears'} onChange={handleChange} className="mt-1 text-blue-600" />
                                    <span className="text-sm text-gray-700"><span className="font-medium">Bill in arrears</span><br /><span className="text-xs text-gray-500">Each invoice covers the period just finished (a Feb 1 invoice covers January).</span></span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* NOTES */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 space-y-3">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Work Performed <span className="normal-case font-normal text-gray-400">(shown on invoice)</span></label>
                            <textarea name="workPerformed" value={scheduleData.workPerformed} onChange={handleChange} rows="2" className="w-full p-3 border rounded-xl text-sm" placeholder="Describe the recurring service..." />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Internal Notes</label>
                            <textarea name="notes" value={scheduleData.notes} onChange={handleChange} rows="2" className="w-full p-3 border rounded-xl text-sm" placeholder="Add memo..." />
                        </div>
                    </div>
                </div>

                {/* FOOTER */}
                <div className="p-4 border-t bg-white flex flex-col md:flex-row justify-between items-center gap-4 flex-shrink-0">
                    <div className="w-full md:w-auto">
                        {error && <p className="text-red-500 text-xs mb-1 flex items-center gap-1"><AlertTriangle size={12} /> {error}</p>}
                        <div className="text-xs text-gray-500">Amount per invoice</div>
                        <div className="text-2xl font-bold text-blue-600 leading-none">{formatCurrency(calculations.total, companySettings?.currency || 'USD')}</div>
                    </div>
                    <div className="flex gap-3 w-full md:w-auto">
                        <button onClick={() => closeModal()} className="flex-1 md:flex-none py-3 px-6 rounded-xl text-gray-600 font-medium hover:bg-gray-100">Cancel</button>
                        <button onClick={handleSave} disabled={isSaving} className="flex-1 md:flex-none py-3 px-8 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                            {isSaving ? <Loader2 size={18} className="animate-spin" /> : (isEdit ? 'Save Changes' : 'Create Schedule')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecurringInvoiceFormModal;
